import { Badge, Button, Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "../../api/auth";
import { useErrorText } from "../../api/errors";
import { useTaskAction, type Task } from "../../api/tasks";
import { assigneeText, taskTitle } from "./taskText";

/** Karta úkolu: splnění jedním klepnutím, převzetí, přeskočení (zůstává vidět, AC-7), vrácení před kontrolou. */
export function TaskCard({ task }: { task: Task }) {
  const { t, i18n } = useTranslation();
  const errorText = useErrorText();
  const me = useCurrentUser();
  const action = useTaskAction();
  const [expanded, setExpanded] = useState(false);
  const isOpen = task.status === "Open";
  const claimedByOther = task.claimedBy && task.claimedBy !== me.id;
  const run = (kind: Parameters<typeof action.mutate>[0]["action"]) =>
    action.mutate({ id: task.id, action: kind }, { onError: (e) => notifications.show({ color: "red", message: errorText(e) }) });

  return (
    <Paper withBorder p="sm" opacity={isOpen ? 1 : 0.65}
      style={task.skipped && isOpen ? { borderColor: "var(--mantine-color-orange-5)", borderWidth: 2 } : undefined}>
      <Stack gap={6}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <UnstyledButton onClick={() => setExpanded((e) => !e)} style={{ flex: 1 }}>
            <Text fw={600} td={isOpen ? undefined : "line-through"}>
              {taskTitle(t, task)}
            </Text>
            <Text size="xs" c="dimmed">
              {assigneeText(t, task)}
              {task.periodKind !== "Day" && ` · ${t("tasks.until", { date: new Date(task.periodEnd).toLocaleDateString(i18n.language) })}`}
              {task.periodKind === "Week" && task.periodStart === task.periodEnd &&
                ` · ${new Date(task.periodStart).toLocaleDateString(i18n.language, { weekday: "long" })}`}
            </Text>
          </UnstyledButton>
          <Group gap={4}>
            {task.kind === "Prep" && <Badge color="teal" variant="light">{t("tasks.kind.Prep")}</Badge>}
            {task.skipped && isOpen && (
              <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>{t("tasks.skipped")}</Badge>
            )}
            {task.status === "Verified" && <Badge color="green">{t("tasks.status.Verified")}</Badge>}
            {task.status === "Rejected" && <Badge color="red">{t("tasks.status.Rejected")}</Badge>}
            {task.status === "Missed" && <Badge color="gray">{t("tasks.status.Missed")}</Badge>}
          </Group>
        </Group>

        {expanded && task.description && <Text size="sm">{task.description}</Text>}
        {claimedByOther && isOpen && <Text size="xs" c="blue.7">{t("tasks.claimedBy", { name: task.claimedByName })}</Text>}
        {!isOpen && task.completedByName && (
          <Text size="xs" c="dimmed">
            {t("tasks.completedBy", { name: task.completedByName })}
            {task.reviewNote && ` · ${task.reviewNote}`}
          </Text>
        )}

        {isOpen ? (
          <Group gap={6} wrap="nowrap">
            <Button color="green" size="md" px="xs" style={{ flex: 1, minWidth: 0 }}
              loading={action.isPending && action.variables?.action === "complete"} onClick={() => run("complete")}>
              {t("tasks.complete")}
            </Button>
            {!task.claimedBy && <Button variant="light" size="md" px="sm" onClick={() => run("claim")}>{t("tasks.claim")}</Button>}
            {task.claimedBy === me.id && <Button variant="subtle" size="md" px="sm" onClick={() => run("unclaim")}>{t("tasks.unclaim")}</Button>}
            {!task.skipped && <Button variant="subtle" color="orange" size="md" px="xs" onClick={() => run("skip")}>{t("tasks.skip")}</Button>}
          </Group>
        ) : (
          task.status === "Done" && task.completedBy === me.id && (
            <Group>
              <Button variant="subtle" size="xs" onClick={() => run("reopen")}>{t("tasks.reopen")}</Button>
            </Group>
          )
        )}
      </Stack>
    </Paper>
  );
}
